<template>
  <div :class="['pta-card-session', simpleCard && 'pta-card-session--no-bg']">
    <CardBackground v-if="!simpleCard" class="pta-card-session__bg" />
    <div
      :class="[
        'pta-card-session__body',
        simpleCard && 'pta-card-session__body--expanded',
      ]"
    >
      <div class="pta-card-session__title-wrapper">
        <PTACardGlobalSession
          v-if="isOtherSessionRunning && !loading"
          class="pta-card-session__session-details"
          v-bind="{
            currentLab,
            currentLabSession,
          }"
        />
      </div>
      <div class="pta-card-session__actions-wrapper">
        <div
          v-if="!isStartingLab && !isLoadingSession"
          :class="[
            'pta-card-session__buttons',
            simpleCard && 'pta-card-session__buttons--expanded',
          ]"
        >
          <div class="pta-card-session__title-group">
            <slot name="title"></slot>
          </div>
          <div
            v-if="!labError"
            class="pta-card-session__region-selector__wrapper"
          >
            <h3 v-if="!simpleCard" class="pta-card-session__subtitle">
              Select the region closest to you and your keyboard layout, then
              start the lab.
            </h3>
            <Dropdown
              class="pta-card-session__region-selector"
              :options="regions"
              :handleSelection="regionDropdownHandler"
              :selectedOption="regionSelected"
            />
            <Dropdown
              class="pta-card-session__region-selector"
              :options="possibleKeyboards"
              :handleSelection="keyboardDropdownHandler"
              :selectedOption="keyboardSelected"
            />
          </div>
          <div v-else class="pta-card-session__region-selector__wrapper">
            <h3
              class="pta-card-session__subtitle pta-card-session__subtitle--error"
            >
              Lab failed to start. Wait a moment and try again. If the problem
              persists, contact support.
            </h3>
          </div>
          <slot name="info"></slot>
          <div
            v-tooltip="{
              content: isCtaDisabled
                ? `Complete required fields<br/>to start the lab`
                : null,
              placement: 'top-center',
              classes: ['home-bootcamp__tooltip'],
            }"
            class="pta-card-session__action-buttons"
            :class="isCtaDisabled && 'pta-card-session__btn--disabled'"
          >
            <button
              class="pta-card-session__btn btn btn--primary"
              :disabled="isCtaDisabled"
              @click="handleSession"
            >
              <slot name="ctaHandler">{{ ctaTexts[sessionStatus] }}</slot>
            </button>
          </div>
        </div>
        <div v-else class="pta-card-session__loader-wrapper">
          <ActionLoader name="ball-beat" color="'#ffffff" />
          <h3 class="pta-card-session__lab-info-label">
            {{ loaderLabel }}
          </h3>
        </div>
      </div>
    </div>
    <DialogModal v-if="showIdleModal" @close-dialog-modal="closeModal">
      <p class="dialog-modal__title">Are you still working on this lab?</p>
      <div class="dialog-modal__actions-wrapper">
        <button class="btn btn--primary" @click="handlePingInterval">
          Yes
        </button>
        <button class="btn btn--secondary" @click="closeModal">No</button>
      </div>
    </DialogModal>
  </div>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import DialogModal from '@/components/modals/DialogModal.vue'
import CONSTANTS from '@/constants'
import Dropdown from '@/components/forms/elements/Dropdown.vue'
import ActionLoader from '@/components/shared/ActionLoader.vue'
import PTACardGlobalSession from './PTACardGlobalSession.vue'
import CardBackground from './images/start-lab-bg.svg'

export default {
  name: 'CardCurrentSession',
  components: {
    DialogModal,
    PTACardGlobalSession,
    Dropdown,
    ActionLoader,
    CardBackground,
  },
  props: {
    parentId: {
      type: String,
      required: true,
    },
    parentType: {
      type: String,
      required: true,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    loadingLabel: {
      type: String,
      required: false,
      default: '',
    },
    simpleCard: {
      type: Boolean,
      required: false,
      default: false,
    },
    labError: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  data: () => {
    return {
      sessionRefreshIntervalId: null,
      sessionPingIntervalId: null,
      runningTime: null,
      runningTimeIntervalId: null,
      refreshSeconds: 5,
      pingSeconds: 60,
      showIdleModal: false,
      timeIdleModal: 0,
      markingActive: null,
      isLabIframeEnabled: false,
      ctaTexts: {
        iddle: 'Start lab',
        starting: 'Start lab',
        started: 'Stop lab',
        stopping: 'Stop lab',
        stopped: 'Resume lab',
        terminating: 'Start lab',
        terminated: 'Start lab',
      },
      stoppingLab: false, // we force this state to show loading
      waitingIntervalId: null,
      waitingTime: CONSTANTS.LABS.FIXED_PARAMS.WAITING_TIME,
      regionSelected: '',
      keyboardSelected: {
        label: 'English (US)',
        code: 'en-us-qwerty',
        flag: '🇺🇸',
      },
      possibleKeyboards: [
        {
          label: 'Brazilian (Portuguese)',
          code: 'pt-br-qwerty',
          flag: '🇧🇷',
        },
        {
          label: 'English (UK)',
          code: 'en-gb-qwerty',
          flag: '🇬🇧',
        },
        {
          label: 'English (US)',
          code: 'en-us-qwerty',
          flag: '🇺🇸',
        },
        {
          label: 'Danish',
          code: 'da-dk-qwerty',
          flag: '🇩🇰',
        },
        {
          label: 'French',
          code: 'fr-fr-azerty',
          flag: '🇫🇷',
        },
        {
          label: 'French (Belgian)',
          code: 'fr-be-azerty',
          flag: '🇫🇷',
        },
        {
          label: 'French (Swiss)',
          code: 'fr-ch-qwertz',
          flag: '🇫🇷',
        },
        {
          label: 'German',
          code: 'de-de-qwertz',
          flag: '🇩🇪',
        },
        {
          label: 'German (Swiss)',
          code: 'de-ch-qwertz',
          flag: '🇩🇪',
        },
        {
          label: 'Hungarian',
          code: 'hu-hu-qwertz',
          flag: '🇭🇺',
        },
        {
          label: 'Italian',
          code: 'it-it-qwerty',
          flag: '🇮🇹',
        },
        {
          label: 'Japanese',
          code: 'ja-jp-qwerty',
          flag: '🇯🇵',
        },
        {
          label: 'Norwegian',
          code: 'no-no-qwerty',
          flag: '🇳🇴',
        },
        {
          label: 'Spanish',
          code: 'es-es-qwerty',
          flag: '🇪🇸',
        },
        {
          label: 'Spanish (Latin American)',
          code: 'es-latam-qwerty',
          flag: '🇪🇸',
        },
        {
          label: 'Swedish',
          code: 'sv-se-qwerty',
          flag: '🇸🇪',
        },
        {
          label: 'Turkish-Q',
          code: 'tr-tr-qwerty',
          flag: '🇹🇷',
        },
      ],
    }
  },
  computed: {
    ...mapState({
      currentLabSession: state => state.labs.currentLabSession,
      currentLab: state => state.labs.currentLab,
    }),
    ...mapGetters({
      isStartingLab: 'labs/isStartingLab',
      isTransitioningStates: 'labs/isTransitioningStates',
    }),
    regions() {
      return CONSTANTS.LABS.REGIONS
    },
    labSessionRunning() {
      if (!this.sessionStatus) return false
      const sessionTransitioningState = [CONSTANTS.LABS.SESSION_STATUS.STARTED]
      return sessionTransitioningState.includes(this.sessionStatus)
    },
    sessionStatus() {
      if (this.isStartingLab || (this.waitingIntervalId && !!this.waitingTime))
        return CONSTANTS.LABS.SESSION_STATUS.STARTING
      if (this.stoppingLab) return CONSTANTS.LABS.SESSION_STATUS.STOPPING
      return this.currentLabSession &&
        this.currentLabSession.status &&
        !this.sessionsToIgnore.includes(this.currentLabSession?.status)
        ? this.currentLabSession.status
        : CONSTANTS.LABS.SESSION_STATUS.IDDLE
    },
    sessionsToIgnore() {
      return [
        CONSTANTS.LABS.SESSION_STATUS.TERMINATED,
        CONSTANTS.LABS.SESSION_STATUS.ERROR,
      ]
    },
    globalRunningSession() {
      return this.currentLab.global_running_session
    },
    isOtherSessionRunning() {
      const currentLabSessionId = this.currentLabSession
        ? this.currentLabSession.id
        : null
      return (
        this.globalRunningSession &&
        this.globalRunningSession.status !==
          CONSTANTS.LABS.SESSION_STATUS.TERMINATED &&
        this.globalRunningSession.id !== currentLabSessionId
      )
    },
    shouldCreateRefreshInterval() {
      const sessionRefreshStatuses = [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.STOPPING,
      ]
      return sessionRefreshStatuses.includes(this.currentLabSession?.status)
    },
    shouldCreatePingInterval() {
      const sessionPingStatuses = [
        CONSTANTS.LABS.SESSION_STATUS.STARTING,
        CONSTANTS.LABS.SESSION_STATUS.STARTED,
      ]
      return sessionPingStatuses.includes(this.sessionStatus)
    },
    noRegionSelected() {
      return !this.regionSelected
    },
    isCtaDisabled() {
      return (
        this.isOtherSessionRunning ||
        this.isTransitioningStates ||
        this.noRegionSelected
      )
    },
    loaderLabel() {
      if (this.loadingLabel) return this.loadingLabel
      return this.isStartingLab && !this.simpleCard ? 'Lab starting' : ''
    },
    labsRequestPayload() {
      return {
        id: this.currentLab.id,
        parentId: this.parentId,
        parentType: this.parentType,
      }
    },
    isLoadingSession() {
      return this.loading || this.isTransitioningStates
    },
  },
  watch: {
    currentLabSession: {
      immediate: true,
      handler() {
        this.handleRefreshInterval()
        this.handlePingInterval()
        this.handleRunningTimeInterval()
      },
    },
    sessionStatus: {
      immediate: true,
      handler() {
        this.$emit('status-has-changed', this.sessionStatus)
      },
    },
  },
  beforeDestroy() {
    this.sessionRefreshIntervalId = clearInterval(this.sessionRefreshIntervalId)
    this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
    this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
    this.markingActive = clearTimeout(this.markingActive)
  },
  mounted() {
    this.regionSelected = localStorage.getItem(`pta-lab-region`) || 'US-East'
  },
  methods: {
    async handleSession() {
      if (this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.IDDLE) {
        await this.createSession()
      } else if (this.sessionStatus === CONSTANTS.LABS.SESSION_STATUS.STARTED) {
        this.stoppingLab = true
        this.$emit(
          'update-session-status',
          CONSTANTS.LABS.SESSION_STATUS.TERMINATED
        )
        this.stoppingLab = false
      }
    },
    async createSession() {
      const payload = {
        regionSelected: this.regionSelected,
        keyboardSelected: this.keyboardSelected,
        ...this.labsRequestPayload,
      }
      this.$emit('create-session', payload)
      localStorage.setItem(`pta-lab-region`, this.regionSelected)
      localStorage.setItem(`pta-lab-keyboard`, this.keyboardSelected?.code)
      this.handleWaitingTime()
    },
    async getSession() {
      try {
        await this.$store.dispatch('labs/getLabSession', {
          labId: this.currentLab.id,
          sessionId: this.currentLabSession.id,
          ...this.labsRequestPayload,
        })
      } catch (error) {
        this.sessionRefreshIntervalId = clearInterval(
          this.sessionRefreshIntervalId
        )
      }
    },
    ctaTextsLabel(sessionStatus) {
      return this.ctaTexts[sessionStatus]
    },
    async createPing() {
      const response = await this.$store.dispatch(
        'labs/createLabPing',
        this.labsRequestPayload
      )
      if (response) {
        // if error we should get Lab to update session if there is any, this will stop future pings with shouldCreatePingInterval
        await this.$store.dispatch('labs/getLabById', this.labsRequestPayload)
      }
    },
    increaseRunningTime() {
      this.runningTime += 1
      this.timeIdleModal += 1
      this.checkIdleTime()
    },
    decreaseWaitingTime() {
      this.waitingTime -= 1
      this.checkWaitingTime()
    },
    setInitialRunningTime() {
      this.runningTime = this.currentLabSession
        ? this.currentLabSession.running_time
        : 0
    },
    setInitialWaitingTime() {
      this.waitingTime = CONSTANTS.LABS.FIXED_PARAMS.WAITING_TIME
    },
    handleRefreshInterval() {
      if (this.shouldCreateRefreshInterval && !this.sessionRefreshIntervalId) {
        this.sessionRefreshIntervalId = setInterval(
          this.getSession,
          this.refreshSeconds * 1000
        )
      } else if (!this.shouldCreateRefreshInterval) {
        this.sessionRefreshIntervalId = clearInterval(
          this.sessionRefreshIntervalId
        )
      }
    },
    handleWaitingTime() {
      this.setInitialWaitingTime()
      if (this.waitingTime > 1) {
        this.waitingIntervalId = setInterval(this.decreaseWaitingTime, 1000)
      } else {
        this.waitingIntervalId = clearInterval(this.waitingIntervalId)
        this.handleRunningTimeInterval()
      }
    },
    handlePingInterval() {
      if (this.shouldCreatePingInterval && !this.sessionPingIntervalId) {
        this.sessionPingIntervalId = setInterval(
          this.createPing,
          this.pingSeconds * 1000
        )
        this.timeIdleModal = 0
        this.showIdleModal = false
      } else if (!this.shouldCreatePingInterval) {
        this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
      }
    },
    handleRunningTimeInterval() {
      this.setInitialRunningTime()
      if (this.sessionStatus === 'started' && !this.runningTimeIntervalId) {
        this.runningTimeIntervalId = setInterval(this.increaseRunningTime, 1000)
      } else if (this.sessionStatus !== 'started') {
        this.runningTimeIntervalId = clearInterval(this.runningTimeIntervalId)
      }
    },
    closeModal() {
      this.showIdleModal = false
      this.$emit('close-modal')
    },
    checkIdleTime() {
      if (this.timeIdleModal >= this.currentLab.duration_in_seconds * 2) {
        this.sessionPingIntervalId = clearInterval(this.sessionPingIntervalId)
        this.showIdleModal = true
      }
    },
    checkWaitingTime() {
      if (this.waitingTime < 1) {
        this.waitingIntervalId = clearInterval(this.waitingIntervalId)
        this.handleRunningTimeInterval()
      }
    },
    regionDropdownHandler(regionSelected) {
      this.regionSelected = regionSelected
    },
    keyboardDropdownHandler(keyboardSelected) {
      this.keyboardSelected = keyboardSelected
    },
  },
}
</script>

<style lang="scss" scoped>
.pta-card-session {
  position: relative;
  max-width: 1240px;
  padding: 20px;
  color: var(--light-blue-gray);
  min-height: 280px;
  display: flex;
  flex: 1;
  flex-wrap: nowrap;
  background-image: var(--glass-purple);
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 10px;
  padding: 155px 0;
  &--no-bg {
    background-image: none;
  }
  &__bg {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    z-index: 0;
  }
  &__loader-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
  }
  &__lab-info-label {
    color: var(--white);
    margin: 0;
    font-size: rem(16);
    line-height: rem(24);
    font-weight: 500;
  }
  &__body {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    &--expanded {
      gap: 20px;
    }
  }
  &__title {
    margin: 0;
    display: flex;
    align-content: center;
    align-items: center;
    font-size: rem(18);
    line-height: rem(26);
    font-weight: 500;
    color: var(--white);
    position: relative;
    &-group {
      margin-bottom: 10px;
    }
  }
  &__subtitle {
    margin: 0;
    font-size: rem(16);
    line-height: rem(24);
    color: var(--white);
    margin-bottom: 20px;
    text-align: center;
    &--error {
      margin-bottom: 10px;
    }
  }
  &__status-icon {
    margin-left: 10px;
  }
  &__lab-status {
    display: flex;
    align-items: center;
    margin-top: 20px;
  }
  &__btn {
    &--disabled {
      cursor: not-allowed;
    }
  }
  &__buttons {
    width: 100%;
    text-align: right;
    &__open-lab {
      margin-left: 15px;
    }
    &--expanded {
      text-align: left;
      display: flex;
      flex-direction: column;
      gap: 20px;
      .pta-card-session__region-selector {
        width: 100%;
        max-width: 100%;
      }
      .pta-card-session__action-buttons {
        justify-content: flex-end;
      }
    }
  }
  &__region-selector__wrapper {
    display: flex;
    flex-direction: column;
    gap: 10px;
    margin-bottom: 20px;
    align-items: center;
  }
  &__region-info-wrapper {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: var(--white);
    margin: 0;
    span {
      color: var(--carnation);
    }
  }
  &__region-info-icon {
    width: 14.7px;
    cursor: pointer;
    margin-right: 5px;
  }
  &__region-info-error {
    color: var(--carnation);
    margin-left: auto;
  }
  &__region-info-tooltip {
    max-width: 250px;
    font-size: 14px;
    line-height: 20px;
    text-align: center;
  }
  &__action-buttons {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    &--disabled {
      cursor: not-allowed;
    }
  }
  &__actions {
    margin-left: auto;
    align-self: center;
    &-wrapper {
      display: flex;
      flex-direction: column;
    }
  }
  &__time-running {
    color: var(--white);
    width: 100%;
  }
  &__flat-btn {
    background: transparent;
    color: var(--white);
    border: none;
    display: flex;
    align-items: center;
    padding: 0px;
    cursor: pointer;
    &:disabled {
      cursor: not-allowed;
      .pta-card-session__flat-btn-text {
        color: var(--lighter-gray);
        border-color: var(--lighter-gray);
      }
    }
    &-text {
      font-size: 12px;
      font-weight: 700;
      border-bottom: 1px solid var(--white);
      margin: 0px 5px;
    }
  }
  &__check-icon {
    min-width: 16px;
    circle {
      fill: var(--lighter-gray);
    }
    &--success {
      circle {
        fill: var(--lab-status-green);
      }
    }
  }
  &__info-icon {
    color: var(--lighter-gray);
    font-size: 16px;
    margin-right: 5px;
  }
  &__session-details {
    margin-top: 30px;
  }
  &__footer {
    background: linear-gradient(
      112.2deg,
      rgba(187, 188, 199, 0.4) -46.39%,
      rgba(187, 188, 199, 0) 112.38%
    );
    border-radius: 0px 0px 10px 10px;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    padding: 5px;
    align-items: center;
    font-size: 12px;
    line-height: 14px;
    &__pill {
      padding: 9px 14px;
      background: linear-gradient(
        111.02deg,
        rgba(18, 18, 18, 0.7) 3.86%,
        rgba(18, 18, 18, 0) 107.96%
      );
      border-radius: 20px;
      margin-right: 10px;
      color: var(--white);
    }
  }
  &__region-selector {
    min-width: 220px;
    max-width: 300px;
    &__icon {
      position: absolute;
      right: 20px;
      top: 50%;
      transform: translate(0, -50%);
      pointer-events: none;
    }
  }
  @keyframes rotation {
    from {
      -webkit-transform: rotate(0deg);
    }
    to {
      -webkit-transform: rotate(359deg);
    }
  }
  .modal-message {
    margin: auto;
  }
}
</style>
